import { MutationObserver } from "@tanstack/query-core";
import type { CreatePostParams } from "api-contracts/rpc/create-post";
import type { DeletePostParams } from "api-contracts/rpc/delete-post";
import type { UpdatePostParams } from "api-contracts/rpc/update-post";
import type { UpdateContentTierResult } from "api-contracts/rpc/update-content-tier";
import { queryClient } from "ui-common/data-query-controller";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification/notification-service";

export const createPostMutation = new MutationObserver(queryClient, {
  mutationKey: ["createPost"],
  mutationFn: async (params: CreatePostParams) => {
    const result = await rpc("createPost", params);

    if (!result?.postId) {
      return result;
    }

    // Handle premium tier if specified
    if (params.contentTier === "premium") {
      const tierResponse = (await rpc("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "premium",
      })) as UpdateContentTierResult;

      // Show notification if subscription tier was auto-created with default price
      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to €${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    }

    // Handle one-time purchase if specified
    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await rpc("enable-one-time-purchase", {
        contentType: "post",
        contentId: result.postId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Post - ${params.description.substring(0, 50)}`,
      });
    }

    return result;
  },
  onSuccess: () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["posts"] }),
      queryClient.invalidateQueries({ queryKey: ["worlds"] }),
    ]),
});

export const updatePostMutation = new MutationObserver(queryClient, {
  mutationKey: ["updatePost"],
  mutationFn: async (params: UpdatePostParams) => {
    const result = await rpc("updatePost", params);

    if (!result?.postId) {
      return result;
    }

    // Handle premium tier if specified
    if (params.contentTier === "premium") {
      const tierResponse = (await rpc("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "premium",
      })) as UpdateContentTierResult;

      // Show notification if subscription tier was auto-created with default price
      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to €${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    } else if (params.contentTier === "free") {
      // Downgrade to free if specified
      console.log("[POST MUTATION] Downgrading post to free tier", result.postId);
      await rpc("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "free",
      });
      console.log("[POST MUTATION] Downgrade RPC completed");
    }

    // Handle one-time purchase
    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await rpc("enable-one-time-purchase", {
        contentType: "post",
        contentId: result.postId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Post - ${params.teaser.substring(0, 50)}`,
      });
    }
    // Note: One-time products are left in Stripe when not specified
    // They can be managed separately or archived if needed

    return result;
  },
  onSuccess: () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["posts"] }),
      queryClient.invalidateQueries({ queryKey: ["worlds"] }),
    ]),
});

export const deletePostMutation = new MutationObserver(queryClient, {
  mutationKey: ["deletePost"],
  mutationFn: (params: DeletePostParams) => rpc("deletePost", params),
  onSuccess: () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["posts"] }),
      queryClient.invalidateQueries({ queryKey: ["worlds"] }),
    ]),
});
